import { create } from 'zustand'
import { jobsService, bookingsService } from '@/lib/api/services'
import type { Job, CreateJobData, UpdateJobData, CreateIndependentBookingData } from '../types/job'

// Normalize job data from API response to match the Job interface
// The backend returns nested contact/service objects, but we need flat fields
const normalizeJob = (apiJob: any): Job => {
  // If contact info is already flattened (has contactName), return as-is
  if (apiJob.contactName) {
    return apiJob as Job
  }
  
  // Extract contact info from nested contact object if present
  const contact = apiJob.contact
  const service = apiJob.service
  
  return {
    id: apiJob.id,
    title: apiJob.title,
    description: apiJob.description,
    contactId: apiJob.contactId,
    contactName: contact 
      ? `${contact.firstName || ''} ${contact.lastName || ''}`.trim() 
      : '',
    contactEmail: contact?.email,
    contactPhone: contact?.phone,
    serviceId: apiJob.serviceId,
    serviceName: service?.name,
    quoteId: apiJob.quoteId,
    invoiceId: apiJob.invoiceId,
    recurrenceId: apiJob.recurrenceId,
    startTime: apiJob.startTime,
    endTime: apiJob.endTime,
    toBeScheduled: apiJob.toBeScheduled || false,
    status: apiJob.status,
    location: apiJob.location,
    price: apiJob.price != null ? (typeof apiJob.price === 'number' ? apiJob.price : parseFloat(apiJob.price)) : null,
    notes: apiJob.notes,
    assignedTo: apiJob.assignedTo,
    assignedToName: apiJob.assignedToUser?.name ?? apiJob.assignedToName ?? undefined,
    breaks: apiJob.breaks || undefined,
    deletedAt: apiJob.deletedAt,
    archivedAt: apiJob.archivedAt,
    createdById: apiJob.createdById,
    createdByName: apiJob.createdBy?.name ?? apiJob.createdByName ?? null,
    createdAt: apiJob.createdAt,
    updatedAt: apiJob.updatedAt,
    occurrenceCount: apiJob.occurrenceCount,
    bookingId: apiJob.bookingId ?? undefined,
    isIndependent: apiJob.isIndependent ?? false,
    isStagedSeries: apiJob.isStagedSeries ?? undefined,
    seriesStartMonth: apiJob.seriesStartMonth ?? undefined,
    anchorBookingId: apiJob.anchorBookingId ?? undefined,
    jobId: apiJob.jobId ?? undefined,
  }
}

interface JobState {
  jobs: Job[]
  jobsForLink: Job[] // All jobs including unlinked - for "link to existing job" dropdown
  selectedJob: Job | null
  isLoading: boolean
  error: string | null
  viewMode: 'day' | 'week' | 'month'
  currentDate: Date
  jobView: 'active' | 'archived' | 'trash'
  
  // Actions
  fetchJobs: (startDate?: Date, endDate?: Date, includeArchived?: boolean, showDeleted?: boolean) => Promise<void>
  fetchJobsForLink: (startDate?: Date, endDate?: Date) => Promise<void>
  getJobById: (id: string) => Promise<void>
  createJob: (data: CreateJobData) => Promise<Job>
  createIndependentBooking: (data: CreateIndependentBookingData) => Promise<Job>
  updateJob: (data: UpdateJobData) => Promise<void>
  updateIndependentBooking: (id: string, data: Partial<CreateIndependentBookingData>) => Promise<void>
  deleteJob: (id: string, deleteAll?: boolean) => Promise<void>
  permanentDeleteJob: (id: string, deleteAll?: boolean) => Promise<void>
  restoreJob: (id: string, opts?: { bookingId?: string; isIndependent?: boolean }) => Promise<void>
  setSelectedJob: (job: Job | null) => void
  setViewMode: (mode: 'day' | 'week' | 'month') => void
  setCurrentDate: (date: Date) => void
  setJobView: (view: 'active' | 'archived' | 'trash') => void
  confirmJob: (id: string, notifyClient?: boolean) => Promise<void>
  declineJob: (id: string, reason?: string) => Promise<void>
  clearError: () => void
}

export const useJobStore = create<JobState>((set, get) => ({
  jobs: [],
  jobsForLink: [],
  selectedJob: null,
  isLoading: false,
  error: null,
  viewMode: 'month',
  currentDate: new Date(),
  jobView: 'active',

  fetchJobs: async (startDate?: Date, endDate?: Date, includeArchived?: boolean, showDeleted?: boolean) => {
    set({ isLoading: true, error: null })
    try {
      const apiJobs = await jobsService.getAll(startDate, endDate, includeArchived, showDeleted)
      const jobs = apiJobs.map(normalizeJob)
      set({ jobs, isLoading: false })
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch jobs',
        isLoading: false,
      })
    }
  },

  fetchJobsForLink: async (startDate?: Date, endDate?: Date) => {
    try {
      const apiJobs = await jobsService.getAll(startDate, endDate, false, false, true)
      const jobsForLink = apiJobs.map(normalizeJob)
      set({ jobsForLink })
    } catch (error: any) {
      set({ jobsForLink: [] })
    }
  },

  getJobById: async (id: string) => {
    set({ isLoading: true, error: null })
    try {
      const apiJob = await jobsService.getById(id)
      const job = normalizeJob(apiJob)
      set({ selectedJob: job, isLoading: false })
    } catch (error: any) {
      set({
        error: error.message || 'Failed to fetch job',
        isLoading: false,
      })
    }
  },

  createJob: async (data: CreateJobData) => {
    set({ isLoading: true, error: null })
    try {
      const apiJob = await jobsService.create(data)
      
      // If this is a recurring job (has occurrenceCount > 1), refresh all jobs
      // to get all the created instances. Otherwise just add the single job.
      if (apiJob.occurrenceCount && apiJob.occurrenceCount > 1) {
        // Recurring job - refresh with a wider date range to include all instances
        // Fetch 6 months forward and 1 month back from the current view
        const { currentDate } = get()
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 7, 0)
        await get().fetchJobs(startDate, endDate)
        // Return the first job from the recurring series
        const newJob = normalizeJob(apiJob)
        return newJob
      } else {
        // Single job - just add it to the state
        const newJob = normalizeJob(apiJob)
        set((state) => ({
          jobs: [...state.jobs, newJob],
          isLoading: false,
        }))
        return newJob
      }
    } catch (error: any) {
      // Extract error data from axios response
      const errorData = error.response?.data?.error || error
      const errorMessage = errorData.message || error.message || 'Failed to create job'
      
      set({
        error: errorMessage,
        isLoading: false,
      })
      
      // Preserve the status code and conflicts for the UI layer
      const enhancedError = new Error(errorMessage) as any
      enhancedError.statusCode = errorData.statusCode || error.response?.status
      enhancedError.conflicts = errorData.conflicts
      
      throw enhancedError
    }
  },

  createIndependentBooking: async (data: CreateIndependentBookingData) => {
    set({ isLoading: true, error: null })
    try {
      const payload = {
        isIndependent: true,
        title: data.title,
        contactId: data.contactId && data.contactId.trim() ? data.contactId : undefined,
        serviceId: data.serviceId && data.serviceId.trim() ? data.serviceId : undefined,
        startTime: data.startTime || undefined,
        endTime: data.endTime || undefined,
        toBeScheduled: data.toBeScheduled ?? false,
        status: data.status ?? 'active',
        location: data.location || undefined,
        price: data.price ?? undefined,
        notes: data.notes || undefined,
        assignedTo: data.assignedTo ?? undefined,
        breaks: data.breaks ?? undefined,
        // Forward the "notify the client?" choice so the backend can send the booking
        // confirmation email/SMS on create (matches job-backed manual bookings).
        notifyClient: data.notifyClient ?? undefined,
      }
      const apiBooking = await bookingsService.create(payload)
      const newJob = normalizeJob({
        ...apiBooking,
        id: apiBooking.id,
        bookingId: apiBooking.id,
      })
      set((state) => ({
        jobs: [...state.jobs, newJob],
        isLoading: false,
      }))
      return newJob
    } catch (error: any) {
      const apiMessage = error.response?.data?.error?.message || error.response?.data?.message
      const errorMessage = apiMessage || error.message || 'Failed to create appointment'
      set({
        error: errorMessage,
        isLoading: false,
      })
      throw error
    }
  },

  updateIndependentBooking: async (id: string, data: Partial<CreateIndependentBookingData>) => {
    set({ isLoading: true, error: null })
    try {
      const payload: Record<string, unknown> = {}
      if (data.title !== undefined) payload.title = data.title
      if (data.contactId !== undefined) payload.contactId = data.contactId || null
      if (data.serviceId !== undefined) payload.serviceId = data.serviceId || null
      if (data.startTime !== undefined) payload.startTime = data.startTime
      if (data.endTime !== undefined) payload.endTime = data.endTime
      if (data.toBeScheduled !== undefined) payload.toBeScheduled = data.toBeScheduled
      if (data.status !== undefined) payload.status = data.status
      if (data.location !== undefined) payload.location = data.location
      if (data.price !== undefined) payload.price = data.price
      if (data.notes !== undefined) payload.notes = data.notes
      if (data.assignedTo !== undefined) payload.assignedTo = data.assignedTo
      if (data.breaks !== undefined) payload.breaks = data.breaks
      if (data.notifyClient !== undefined) payload.notifyClient = data.notifyClient
      const apiBooking = await bookingsService.update(id, payload)
      const updatedJob = normalizeJob({
        ...apiBooking,
        id: apiBooking.id,
        bookingId: apiBooking.id,
      })
      set((state) => ({
        jobs: state.jobs.map((j) => (j.id === id ? updatedJob : j)),
        selectedJob:
          state.selectedJob?.id === id ? updatedJob : state.selectedJob,
        isLoading: false,
      }))
    } catch (error: any) {
      const apiMessage = error.response?.data?.error?.message || error.response?.data?.message
      set({
        error: apiMessage || error.message || 'Failed to update appointment',
        isLoading: false,
      })
      throw error
    }
  },

  updateJob: async (data: UpdateJobData) => {
    console.log('🔄 Frontend: Updating job with data:', { 
      id: data.id, 
      updateAll: data.updateAll,
      hasUpdateAll: 'updateAll' in data,
      hasRecurrence: !!data.recurrence
    })
    set({ isLoading: true, error: null })
    try {
      const apiJob = await jobsService.update(data.id, data)
      console.log('✅ Frontend: Job update response received', { hasRecurrenceId: !!apiJob.recurrenceId })
      const updatedJob = normalizeJob(apiJob)
      
      // Check if recurrence was added (response has recurrenceId but we're adding recurrence)
      const wasRecurrenceAdded = data.recurrence && updatedJob.recurrenceId
      
      // If updating all jobs in a recurring series OR adding recurrence, refresh the entire jobs list
      if (data.updateAll || wasRecurrenceAdded) {
        console.log('🔄 Refreshing jobs list after update', { updateAll: data.updateAll, wasRecurrenceAdded })
        const { currentDate } = get()
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 7, 0)
        await get().fetchJobs(startDate, endDate)
      } else {
        // Single job update - when bookingId is provided, replace only that entry to avoid lumping
        const bookingId = data.bookingId ?? (updatedJob as any).bookingId
        set((state) => ({
          jobs: state.jobs.map((j) => {
            if (j.id !== data.id) return j
            if (bookingId && j.bookingId === bookingId) return updatedJob
            if (!bookingId) return updatedJob
            return j
          }),
          selectedJob:
            state.selectedJob?.id === data.id &&
            (state.selectedJob?.bookingId === bookingId || !bookingId)
              ? updatedJob
              : state.selectedJob,
          isLoading: false,
        }))
      }
    } catch (error: any) {
      const apiMessage = error.response?.data?.error?.message || error.response?.data?.message
      const errorMessage = apiMessage || error.message || 'Failed to update job'
      set({
        error: errorMessage,
        isLoading: false,
      })
      throw error
    }
  },

  deleteJob: async (id: string, deleteAll?: boolean) => {
    set({ isLoading: true, error: null })
    try {
      console.log('Store: archiveJob called with id:', id, 'deleteAll:', deleteAll)
      await jobsService.delete(id, deleteAll)
      
      const now = new Date().toISOString()
      
      if (deleteAll) {
        // If archiving all, find the recurrenceId and update all jobs with that recurrenceId
        const job = get().jobs.find(j => j.id === id)
        const recurrenceId = job?.recurrenceId
        console.log('Store: Found job with recurrenceId:', recurrenceId)
        
        set((state) => {
          const updatedJobs = state.jobs.map((j) => {
            if (recurrenceId && j.recurrenceId === recurrenceId) {
              return { ...j, archivedAt: now }
            } else if (!recurrenceId && j.id === id) {
              return { ...j, archivedAt: now }
            }
            return j
          })
          return {
            jobs: updatedJobs,
            selectedJob: state.selectedJob?.id === id ? null : state.selectedJob,
            isLoading: false,
          }
        })
      } else {
        // Archive only the single job
        console.log('Store: Archiving single job')
        set((state) => ({
          jobs: state.jobs.map((j) => 
            j.id === id ? { ...j, archivedAt: now } : j
          ),
          selectedJob:
            state.selectedJob?.id === id ? null : state.selectedJob,
          isLoading: false,
        }))
      }
    } catch (error: any) {
      console.error('Store: Error archiving job:', error)
      const apiMessage = error.response?.data?.error?.message || error.response?.data?.message
      set({
        error: apiMessage || error.message || 'Failed to archive job',
        isLoading: false,
      })
      throw error
    }
  },

  setSelectedJob: (job: Job | null) => {
    set({ selectedJob: job })
  },

  setViewMode: (mode: 'day' | 'week' | 'month') => {
    set({ viewMode: mode })
  },

  setCurrentDate: (date: Date) => {
    set({ currentDate: date })
  },

  setJobView: (view: 'active' | 'archived' | 'trash') => {
    set({ jobView: view })
  },

  permanentDeleteJob: async (id: string, deleteAll?: boolean) => {
    set({ isLoading: true, error: null })
    try {
      console.log('Store: permanentDeleteJob called with id:', id, 'deleteAll:', deleteAll)
      await jobsService.permanentDelete(id, deleteAll)
      
      if (deleteAll) {
        const job = get().jobs.find(j => j.id === id)
        const recurrenceId = job?.recurrenceId
        
        set((state) => ({
          jobs: recurrenceId 
            ? state.jobs.filter((j) => j.recurrenceId !== recurrenceId)
            : state.jobs.filter((j) => j.id !== id),
          selectedJob: state.selectedJob?.id === id ? null : state.selectedJob,
          isLoading: false,
        }))
      } else {
        set((state) => ({
          jobs: state.jobs.filter((j) => j.id !== id),
          selectedJob: state.selectedJob?.id === id ? null : state.selectedJob,
          isLoading: false,
        }))
      }
    } catch (error: any) {
      console.error('Store: Error permanently deleting job:', error)
      set({
        error: error.message || 'Failed to permanently delete job',
        isLoading: false,
      })
      throw error
    }
  },

  restoreJob: async (id: string, opts?: { bookingId?: string; isIndependent?: boolean }) => {
    set({ isLoading: true, error: null })
    try {
      if (opts?.isIndependent) {
        // Independent appointments have no Job row — their flattened id IS the booking id, so
        // POST /jobs/:id/restore would 404. Restore the booking directly.
        await bookingsService.restore(opts.bookingId ?? id)
        // bookings.restore returns only { success, bookingId, jobId } — there's no row to merge,
        // and the archived appointment isn't in the active-view array, so a map patch does nothing
        // (the appointment stayed invisible until a manual reload). Refetch the window to surface it.
        const { currentDate } = get()
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 7, 0)
        await get().fetchJobs(startDate, endDate)
        set((state) => ({
          selectedJob:
            state.selectedJob && state.selectedJob.id === id
              ? { ...state.selectedJob, archivedAt: null }
              : state.selectedJob,
          isLoading: false,
        }))
        return
      }
      const apiJob = await jobsService.restore(id, opts?.bookingId)
      const restoredJob = normalizeJob(apiJob)
      if ((restoredJob.occurrenceCount ?? 1) > 1) {
        // A series restore brings back multiple occurrences — one flattened response row can't
        // represent them all, so refetch the current window.
        const { currentDate } = get()
        const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1)
        const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 7, 0)
        await get().fetchJobs(startDate, endDate)
        set((state) => ({
          selectedJob: state.selectedJob?.id === id ? restoredJob : state.selectedJob,
        }))
      } else {
        // The archived row usually isn't in the active-view array at all — append when missing
        // (the old map-only update silently did nothing and the appointment never reappeared).
        set((state) => {
          const present = state.jobs.some((j) => j.id === id)
          return {
            jobs: present
              ? state.jobs.map((j) => (j.id === id ? restoredJob : j))
              : [...state.jobs, restoredJob],
            selectedJob: state.selectedJob?.id === id ? restoredJob : state.selectedJob,
            isLoading: false,
          }
        })
      }
    } catch (error: any) {
      const apiMessage = error.response?.data?.error?.message || error.response?.data?.message
      set({
        error: apiMessage || error.message || 'Failed to restore job',
        isLoading: false,
      })
      throw error
    }
  },

  confirmJob: async (id: string, notifyClient?: boolean) => {
    set({ isLoading: true, error: null })
    try {
      const apiJob = await jobsService.confirm(id, { notifyClient })
      const confirmedJob = normalizeJob(apiJob)
      set((state) => ({
        jobs: state.jobs.map((j) =>
          j.id === id ? confirmedJob : j
        ),
        selectedJob:
          state.selectedJob?.id === id
            ? confirmedJob
            : state.selectedJob,
        isLoading: false,
      }))
    } catch (error: any) {
      set({
        error: error.message || 'Failed to confirm job',
        isLoading: false,
      })
      throw error
    }
  },

  declineJob: async (id: string, reason?: string) => {
    set({ isLoading: true, error: null })
    try {
      const apiJob = await jobsService.decline(id, { reason })
      const declinedJob = normalizeJob(apiJob)
      set((state) => ({
        jobs: state.jobs.map((j) =>
          j.id === id ? declinedJob : j
        ),
        selectedJob:
          state.selectedJob?.id === id
            ? declinedJob
            : state.selectedJob,
        isLoading: false,
      }))
    } catch (error: any) {
      set({
        error: error.message || 'Failed to decline job',
        isLoading: false,
      })
      throw error
    }
  },

  clearError: () => {
    set({ error: null })
  },
}))

